// GroupSelect.tsx
import { useEffect, useState } from 'react';
import { Grid, TextField, MenuItem, CircularProgress } from '@mui/material';
import axios from 'axios';
import { FieldErrors, UseFormRegister } from 'react-hook-form';
import { TOKEN_KEY, url } from 'src/Helpers/utils/Constant';

type GroupOption = {
  id: number;
  name: string; // could be duration or label; adjust if needed
};

type GroupSelectProps<FormValues> = {
  register: UseFormRegister<FormValues>;
  errors?: FieldErrors<FormValues>;
  name?: keyof FormValues; // default: 'group_id'
  label?: string;
  requiredMessage?: string;
  defaultValue?: number;
};

function GroupSelect<FormValues>({
  register,
  errors,
  name,
  label = 'Group',
  requiredMessage = 'Group is required',
  defaultValue,
}: GroupSelectProps<FormValues>) {
  const [groups, setGroups] = useState<GroupOption[]>([]);
  const [loading, setLoading] = useState(false);

  const token = localStorage.getItem(TOKEN_KEY);

  const fieldName = (name || ('group_id' as keyof FormValues)) as
    | keyof FormValues
    | string;

  const fieldError = errors?.[fieldName as string] as any;

  useEffect(() => {
    const fetchGroups = async () => {
      try {
        setLoading(true);
        const { data } = await axios.get(
          `${url}/api/v1/admin/executive/groups`, // 🔁 change if your endpoint is different
          {
            headers: token
              ? {
                  Authorization: `Bearer ${token}`,
                }
              : undefined,
          }
        );

        if (data?.success && Array.isArray(data.data)) {
          setGroups(
            data.data.map((item: any) => ({
              id: item.id,
              name: item.name || item.duration, // adjust based on your response
            }))
          );
        }
      } catch (err) {
        console.error('Failed to fetch executive groups', err);
      } finally {
        setLoading(false);
      }
    };

    fetchGroups();
  }, [token]);

  return (
    <TextField
      select
      fullWidth
      margin='normal'
      id={String(fieldName)}
      label={label}
      error={!!fieldError}
      helperText={fieldError?.message}
      InputProps={{
        endAdornment: loading ? <CircularProgress size={18} /> : null,
      }}
      defaultValue={defaultValue}
      {...register(fieldName as any, {
        required: requiredMessage,
      })}
    >
      {groups.map((grp) => (
        <MenuItem key={grp.id} value={grp.id}>
          {grp.name}
        </MenuItem>
      ))}
    </TextField>
  );
}

export default GroupSelect;
